import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, UserPlus } from "lucide-react";
import { http } from "@/lib/api";
import { day, useLoad, when } from "@/lib/staff";
import { Card, Field, INPUT, PageHead, RoleBadge, Select, Table } from "@/components/staff/ui";
import { ConfirmAction } from "@/components/staff/ConfirmAction";
import { VerifyStatus } from "@/components/staff/VerifyIdentity";
import { PermissionPicker, startingTicks } from "@/components/staff/PermissionPicker";

function AddStaff({ catalog, onDone }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [perms, setPerms] = useState(() => startingTicks(catalog, "admin"));
  const pickRole = (r) => { setRole(r); setPerms(startingTicks(catalog, r)); };
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const roleName = catalog.roles.find((r) => r.value === role)?.label;
  return (
    <Card title="Add staff" testid="team-add-card">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Their BrynAI email">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className={`${INPUT} w-72 max-w-full`} data-testid="team-add-email" />
        </Field>
        <Field label="Role"><Select value={role} onChange={pickRole} options={catalog.roles.map((r) => [r.value, r.label])} label="Role" testid="team-add-role" /></Field>
      </div>
      <p className="text-xs text-neutral-500 mt-3">Only accounts that have signed in with Google at least once can be staff. The role sets the starting ticks; change any of them below.</p>
      <div className="mt-5"><PermissionPicker catalog={catalog} value={perms} onChange={setPerms} testid="team-add-perm" /></div>
      <div className="mt-5 flex justify-end">
        <ConfirmAction label="Add to staff" icon={UserPlus} primary needsVerify disabled={!valid} testid="team-add-btn" confirmLabel="Add to staff"
          title={`Make ${email.trim()} ${roleName === "Admin" ? "an Admin" : `a ${roleName}`}?`}
          description={`They get ${perms.length} permission${perms.length === 1 ? "" : "s"} straight away. A staff role never changes their study plan.`}
          run={async (reason, token) => {
            await http.post("/staff/team", { email: email.trim(), role, permissions: perms, reason, reauth_token: token });
            toast.success(`${email.trim()} is now on the staff team`);
            setEmail("");
            onDone();
          }} />
      </div>
    </Card>
  );
}

const permCount = (m, total) => (m.role === "owner" ? "Everything" : `${m.permissions.length} of ${total}`);

function columns(total) {
  return [
    { key: "name", label: "Person", render: (m) => <div className="min-w-0"><p className="truncate text-neutral-100">{m.name || "—"}</p><p className="truncate text-xs text-neutral-500">{m.email}</p></div> },
    { key: "role", label: "Role", render: (m) => <RoleBadge role={m.role} testid={`team-role-${m.user_id}`} /> },
    { key: "perms", label: "Permissions", render: (m) => <span className="text-xs text-neutral-300" data-testid={`team-perms-${m.user_id}`}>{permCount(m, total)}</span> },
    { key: "since", label: "Added", render: (m) => <span className="text-xs text-neutral-400">{m.role === "owner" ? "—" : `${day(m.since)}${m.granted_by ? ` by ${m.granted_by}` : ""}`}</span> },
    { key: "last_seen_at", label: "Last active", right: true, render: (m) => when(m.last_seen_at) },
    { key: "open", label: "", right: true, render: () => <ChevronRight className="h-4 w-4 text-neutral-500 inline" aria-hidden="true" /> },
  ];
}

export default function TeamPage() {
  const navigate = useNavigate();
  const { data, error, loading, reload } = useLoad("/staff/team");
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { if (data?.catalog) setCatalog(data.catalog); }, [data]);
  const total = catalog ? catalog.groups.reduce((n, g) => n + g.items.length, 0) : 0;
  return (
    <div data-testid="staff-team" className="space-y-5">
      <PageHead title="Staff & Access" sub="Each person has their own permissions. Changes need a reason and a fresh identity check, and are saved in the audit log.">
        <VerifyStatus />
      </PageHead>
      {catalog && <AddStaff catalog={catalog} onDone={reload} />}
      <Table testid="team-members" columns={columns(total)} rows={data?.members} rowKey={(m) => m.user_id} loading={loading} error={error} onRetry={reload}
        empty="No staff yet." onRowClick={(m) => navigate(`/staff/team/${m.user_id}`)} />
    </div>
  );
}
